<template lang="pug">
div
  .service-limit
    div.text-lg.font-medium.neutral700--text.mt-8 {{ $t('how_many_times_service') }}
    div.text-sm.neutral500--text {{ $t('how_many_times_service_desc') }}
    v-row(no-gutters)
      v-col(cols="12" sm="5")
        v-radio-group(v-model="usageLimitation")
          v-radio(:value="false" color="neutral700")
            template(v-slot:label)
              div.text-md.font-normal.neutral700--text {{ $t('unlimited_times') }}
          v-radio(:value="true" color="neutral700")
            template(v-slot:label)
              div.text-md.font-normal.neutral700--text {{ $t('limited_times') }}

        v-expand-transition
          v-card.round-lg.neutral300.pa-4(v-if="usageLimitation" flat)
            div.text-xs.font-semibold {{ $t('limited_times_desc') }}
            div.d-flex.align-center.mt-2
              div(style="width: 60px")
                v-text-field.text-md.font-normal.round-xl.inputNumber(v-model="usageLimit" placeholder="30" type="number" background-color="basic100" outlined :rules="rules.has_quantity" dense hide-details)
              div.text-xs.neutral700--text.ml-2 {{ $t('times_at_one_time') }}

  .patient-schedule
    div.text-lg.font-medium.neutral700--text.mt-8 {{ $t('when_patients_schedule') }}
    div.text-sm.neutral500--text {{ $t('when_patients_schedule_desc') }}
  
    div.text-md.font-medium.neutral700--text.mt-8 {{ $t('patient_must_schedule') }}...
    div.d-flex.align-center
      div(style="width: 60px")
        v-text-field.text-md.font-normal.mt-2.round-xl.inputNumber.inputText(v-model="timeValue" placeholder="3" type="number" outlined :rules="rules.has_quantity" dense height="48" hide-details)
      div.ml-2(style="width: 200px")
        v-select.text-md.font-normal.mt-2.round-xl(v-model="timeUnit" :items="types" item-text="text" item-value="value" outlined :rules="rules.not_empty_select" dense height="48" hide-details)
          template(v-slot:append)
            eva-icon(name="chevron-down-outline" width="20" height="20" style="margin-top: 6px" :fill="$colors.basic700")
      div.text-sm.neutral700--text.ml-4 {{ $t('before_appointment_start') }}

  .booking-fee
    div.text-lg.font-medium.neutral700--text.mt-8 {{ $t('is_booking_fee_required') }}
    v-row(no-gutters)
      v-col(cols="12" sm="5")
        v-radio-group(v-model="bookingFeeRequired")
          v-radio(:value="false" color="neutral700")
            template(v-slot:label)
              div.text-md.font-normal.neutral700--text {{ $t('no') }}
          v-radio(:value="true" color="neutral700")
            template(v-slot:label)
              div.text-md.font-normal.neutral700--text {{ $t('yes') }}
        
        v-expand-transition
          v-card.round-lg.neutral300.pa-4(v-if="bookingFeeRequired" flat)
            div.text-xs.font-semibold {{ $t('booking_fee_desc') }}
            div(style="width: 120px")
              v-text-field.text-md.font-normal.round-xl.inputNumber.mt-2(v-model="bookingFee" placeholder="100.00" type="number" background-color="basic100" :prefix="$currency.symbol" outlined :rules="rules.has_price" dense hide-details)
  
  div.d-flex.mt-5
    v-spacer
    v-btn.round-lg.heading-6.px-8(v-if="service" color="neutral700" dark depressed height="48" :loading="isLoading" @click="saveData") {{ $t('save') }}

</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'ServiceSettings',
  components: {  },
  inject: ['getRef'],
  data () {
    return {
      isLoading: false,
      usageLimitation: false,
      usageLimit: null,
      timeUnit: 'days',
      timeValue: null,
      types: [
        { text: 'Minutes', value: 'minutes' },
        { text: 'Hour', value: 'hours' },
        { text: 'Day', value: 'days' },
        { text: 'Week', value: 'weeks' },
        { text: 'Month', value: 'months' },
      ],
      bookingFeeRequired: false,
      bookingFee: null,
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 1|| `Amount should be more than ${this.$currency.symbol} 1`],
        not_empty_select: [v => !!v || 'This field is required']
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      service: 'clinic/getService',
    }),
    settingsData () {
      if (!this.service) {
        return {
          usage_limit: this.usageLimitation ? this.usageLimit : null,
          preparation_time_value: this.timeValue,
          preparation_time_unit: this.timeUnit,
          booking_fee: this.bookingFeeRequired ? (this.bookingFee * this.$currency.multiplier) : 0
        }
      }
      return null
    }
  },
  mounted () {
    if (this.service) {
      this.setData(this.service)
    } else {
      this.$watch('settingsData', {
        deep: true,
        handler (val) {
          this.$store.commit('clinic/updateTempService', val)
        } 
      })
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    setData (service) {
      this.usageLimitation = Boolean(service.usage_limit)
      this.usageLimit = service.usage_limit
      this.timeValue = service.preparation_time_value
      this.timeUnit = service.preparation_time_unit
      this.bookingFeeRequired = Boolean(service.booking_fee)
      this.bookingFee = service.booking_fee / this.$currency.multiplier
    },
    saveData () {
      if (!this.getRef.form.validate()) {
        this.showMessage(['error', this.$t('error_message.please_fill_out_all')])
        return
      }
      this.isLoading = true
      let data = {
        usage_limit: this.usageLimitation ? this.usageLimit : null,
        preparation_time_value: this.timeValue,
        preparation_time_unit: this.timeUnit,
        booking_fee: this.bookingFeeRequired ? (this.bookingFee * this.$currency.multiplier) : 0
      }
      const endpoint = `/clinics/${this.company.slug}/services/${this.service.id}`
      this.$axios.patch(endpoint, data)
        .then(resp => {
          this.updateActivityLog(endpoint, data, resp, 'PATCH', this.service.id)
          this.$store.commit('clinic/setService', resp.data)
          this.showMessage(['success', this.$t('success_message.update_successful')])
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    updateActivityLog (endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'service',
        activity: 'Update service (service settings)',
        raw_data: { endpoint, payload, response: response.data, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.file-change-layout {
  position: relative;
}
.input-change-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-basic500);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
.inputNumber >>> input::-webkit-inner-spin-button {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}
.inputText >>> input {
  margin-top: 3px;
  text-align: center;
}
</style>